import {
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    OnInit,
    inject,
    signal,
} from '@angular/core';
import {
    animate,
    AnimationEvent,
    state,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { DEPOSIT_FORM_DIALOG_DATA } from './deposit-form-overlay.tokens';
import { ConfirmDialogComponent } from '../../parts/dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { OutQuint } from '@exl-ng/mulo-common';
import { DepositFormDataService } from '../deposit-form-data.service';
import { DepositFormService } from '../deposit-form.service';
import { ProfileOutputService } from '../../profile/profile-output/profile-output.service';
import { RouterService } from '../../core/router.service';
import { ProfileService } from '../../profile/profile.service';
import { ResearcherOutputListFacet } from '../../shared/interfaces/researcher-output-list-facet.interface';
import { ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { DepositFormComponent } from '../deposit-form.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorSnackbarComponent } from '../../parts/snackbars/error-snackbar/error-snackbar.component';
import { DepositFormOverlayService } from './deposit-form-overlay.service';

const ESCAPE = 27;

@Component({
    selector: 'esp-deposit-form-overlay',
    templateUrl: './deposit-form-overlay.component.html',
    styleUrls: ['./deposit-form-overlay.component.scss'],
    animations: [
        trigger('slideContent', [
            state(
                'void',
                style({
                    transform: 'scale3d(.8,.8,1)',
                    opacity: 0,
                }),
            ),
            state(
                'enter',
                style({
                    transform: 'scale3d(1,1,1)',
                    opacity: 1,
                }),
            ),
            state(
                'leave',
                style({
                    transform: 'scale3d(1.05,1.05,1.05)',
                    opacity: 0,
                }),
            ),
            transition('* => enter', animate('600ms 450ms ' + OutQuint)),
            transition('* => leave', animate('250ms ease-in')),
        ]),
    ],
    imports: [DepositFormComponent, MatButton, MatIcon, TranslateModule],
})
export class DepositFormOverlayComponent implements OnInit {
    private overlaySvc = inject(DepositFormOverlayService);
    image = inject(DEPOSIT_FORM_DIALOG_DATA);
    confirmDialog = inject(MatDialog);
    depositFormDataService = inject(DepositFormDataService);
    depositFormService = inject(DepositFormService);
    private profileOutputService = inject(ProfileOutputService);
    private routerService = inject(RouterService);
    private profileService = inject(ProfileService);
    private focusTrap = inject(ConfigurableFocusTrapFactory);
    private xl8Svc = inject(TranslateService);
    private elementRef = inject(ElementRef);
    private _snackBar = inject(MatSnackBar);

    animationState = signal<'void' | 'enter' | 'leave'>('enter');
    animationStateChanged = new EventEmitter<AnimationEvent>();
    confirmationDialogOpen = false;

    @HostBinding() role = 'dialog';
    @HostBinding('attr.aria-modal') ariaModal = true;
    @HostBinding('attr.aria-label') ariaLabel = 'Researcher deposit form';

    @HostListener('keydown.Escape', ['$event']) private handleKeydown(
        event: KeyboardEvent,
    ) {
        event.stopPropagation();
        if (this.confirmDialog.openDialogs.length === 0) {
            this.onDialogCloseAttempt();
        }
    }

    constructor() {
        const xl8Svc = this.xl8Svc;

        xl8Svc
            .get('research.title.deposit.form')
            .subscribe((_) => (this.ariaLabel = _));
    }
    ngOnInit(): void {
        this.focusTrap
            .create(this.elementRef.nativeElement)
            .focusInitialElement();
    }

    closeOverlay() {
        this.confirmationDialogOpen = false;
        this.overlaySvc.close();
        this.depositFormDataService.resetForm();
        this.depositFormService.updateAmazonInfo();
    }

    onOverlayCloseAttempt(discard?: boolean) {
        if (discard) {
            this.handleDepositDelete();
        }
        this.closeOverlay();
    }

    onAnimationStart(event: AnimationEvent) {
        this.animationStateChanged.emit(event);
    }

    onAnimationDone(event: AnimationEvent) {
        this.animationStateChanged.emit(event);
    }

    startExitAnimation() {
        this.animationState.set('leave');
    }

    saveDraft() {
        const depositData = this.depositFormService.buildDepositData();
        this.depositFormService.saveDraft(depositData.value, false).subscribe({
            next: () => {
                if (this.routerService.isProfileRoute()) {
                    this.refreshDataInOutputListAfterSave();
                }
            },
            error: this.handleError,
        });
    }

    refreshDataInOutputListAfterSave() {
        setTimeout(() => {
            this.profileOutputService
                .getOutputListFacets(this.profileOutputService.isPublicPage())
                .subscribe((data) => {
                    const isListEmpty =
                        this.profileOutputService.facets.length === 0;
                    const facets = data as ResearcherOutputListFacet[];
                    if (facets && facets.length > 0) {
                        this.profileOutputService.saveFacets(data);
                        if (isListEmpty) {
                            //if the is empty- need to navigate to the first facet
                            this.profileOutputService.refreshData$.next(true);
                        } else {
                            this.profileOutputService.refreshData$.next(false);
                        }
                    }
                });
        }, this.profileOutputService.UPDATE_DATA_TIMEOUT);
    }

    onDialogCloseAttempt() {
        if (this.depositFormDataService.category.value) {
            const isDraft = this.depositFormDataService.isDraft();
            const dialog = this.openConfirmationDialog(isDraft);
            this.confirmationDialogOpen = true;
            dialog.afterClosed().subscribe((result) => {
                this.confirmationDialogOpen = false;
                if (result === 'ok') {
                    this.saveDraft();
                    this.closeOverlay();
                } else if (result === 'discard' && !isDraft) {
                    this.handleDepositDelete();
                    this.closeOverlay();
                }
            });
        } else {
            this.closeOverlay();
        }
    }

    openConfirmationDialog(isDraft) {
        return this.confirmDialog.open(ConfirmDialogComponent, {
            maxWidth: '500px',
            data: {
                title: isDraft
                    ? 'research.popup.exit.draft.title'
                    : 'research.popup.exit.save.draft',
                message: isDraft
                    ? 'research.popup.exit.draft.message'
                    : 'research.popup.exit.newdeposit.title',
                actions: {
                    discard: isDraft
                        ? ''
                        : 'research.popup.exit.newdeposit.discard',
                    confirm: 'research.popup.exit.newdeposit.save',
                    cancel: 'research.popup.exit.keep.editing',
                },
                actionFocus: 'confirm',
            },
        });
    }

    discard() {
        if (this.depositFormService.mmsToDeleteInDiscard) {
            this.depositFormService
                .deleteDeposit(this.depositFormService.mmsToDeleteInDiscard)
                .subscribe({ next: () => {}, error: this.handleError });
        }
    }

    private handleDepositDelete() {
        //save the mms in the service- after "closeOverlay" the form will be reset
        this.depositFormService.mmsToDeleteInDiscard =
            this.depositFormDataService.mmsId.value;
        this.discard();
    }

    handleError = (e) => {
        console.error(e);
        this._snackBar.openFromComponent(ErrorSnackbarComponent, {
            horizontalPosition: 'center',
            verticalPosition: 'top',
        });
    };
}
